import { size } from "./size";

export type Breakpoint =
  | "xxl"
  | "xl"
  | "lg"
  | "md"
  | "sm"
  | "xs"
  | "maxLg"
  | "mobile";
export type BreakpointMap = Record<Breakpoint, string>;
export type ScreenMap = Partial<Record<Breakpoint, boolean>>;
export type ScreenSizeMap = Record<Breakpoint, number>;

export const responsiveArray: Breakpoint[] = [
  "xxl",
  "xl",
  "lg",
  "md",
  "sm",
  "xs",
  "maxLg",
  "mobile",
];

export const responsiveMobileBreakpoint = `(max-width: ${size.lg - 1}px)`;
export const responsiveDesktopBreakpoint = `(min-width: ${size.lg}px)`;
export const responsiveXXSBreakpoint = `(max-width: ${size.xxs}px)`;

export const responsiveMap: BreakpointMap = {
  xs: `(max-width: ${size.xs}px)`,
  sm: `(min-width: ${size.sm}px)`,
  md: `(min-width: ${size.md}px)`,
  lg: responsiveDesktopBreakpoint,
  xl: `(min-width: ${size.xl}px)`,
  xxl: `(min-width: ${size.xxl}px)`,
  maxLg: `(max-width: ${size.lg}px)`,
  mobile: responsiveMobileBreakpoint,
};

export const responsiveSizeMap: ScreenSizeMap = size;

type SubscribeFunc = (screens: ScreenMap) => void;
const subscribers = new Map<Number, SubscribeFunc>();
let subUid = -1;
let screens = {};

const EMPTY_SCREENS: ScreenMap = {};
/** 1er getSnapshot client = SSR `{}` ; basculé dès le subscribe sync (avant paint). */
let matchServerSnapshotOnFirstClientRead = typeof window !== "undefined";

const responsiveObserve = {
  matchHandlers: {} as {
    [prop: string]: {
      mql: MediaQueryList;
      listener: ((this: MediaQueryList, ev: MediaQueryListEvent) => any) | null;
    };
  },
  /** HYDRATION-FIX: 1er read client aligné SSR, puis valeurs matchMedia réelles. */
  getSnapshot(): ScreenMap {
    if (typeof window === "undefined") {
      return EMPTY_SCREENS;
    }
    this.register();
    if (matchServerSnapshotOnFirstClientRead) {
      return EMPTY_SCREENS;
    }
    return screens;
  },
  getServerSnapshot(): ScreenMap {
    return EMPTY_SCREENS;
  },
  /** Adapter pour `useSyncExternalStore`. */
  subscribeStore(onStoreChange: () => void): () => void {
    const token = this.subscribe(() => {
      if (matchServerSnapshotOnFirstClientRead) {
        matchServerSnapshotOnFirstClientRead = false;
      }
      onStoreChange();
    });
    return () => this.unsubscribe(token);
  },
  dispatch(pointMap: ScreenMap) {
    screens = pointMap;
    subscribers.forEach((func) => func(screens));
    return subscribers.size >= 1;
  },
  subscribe(func: SubscribeFunc): number {
    if (!subscribers.size) this.register();
    subUid += 1;
    subscribers.set(subUid, func);
    func(screens);
    return subUid;
  },
  unsubscribe(token: number) {
    subscribers.delete(token);
    if (!subscribers.size) this.unregister();
  },
  unregister() {
    // @ts-ignore
    Object.keys(responsiveMap).forEach((screen: Breakpoint) => {
      const matchMediaQuery = responsiveMap[screen];
      const handler = this.matchHandlers[matchMediaQuery];
      if (handler && handler.listener) {
        handler.mql.removeEventListener("change", handler.listener);
      }
    });
    subscribers.clear();
  },
  register() {
    if (typeof window === "undefined") return;
    if (Object.keys(this.matchHandlers).length > 0) return;
    // @ts-ignore
    Object.keys(responsiveMap).forEach((screen: Breakpoint) => {
      const matchMediaQuery = responsiveMap[screen];
      const listener = ({ matches }: { matches: boolean }) => {
        this.dispatch({
          ...screens,
          [screen]: matches,
        });
      };
      const mql = window.matchMedia(matchMediaQuery);
      mql.addEventListener("change", listener);
      this.matchHandlers[matchMediaQuery] = {
        mql,
        listener,
      };

      listener(mql);
    });
  },
};

export default responsiveObserve;
